import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  fetchAdminPlans,
  createAdminPlan,
  updateAdminPlan,
  deleteAdminPlan,
  clearAdminError,
  clearAdminSuccess,
} from "../../redux/slices/adminSlice";
import { SPACING } from "../../theme/constants";

const emptyPlan = {
  name: "",
  priceMonthly: 0,
  priceYearly: 0,
  trialDays: 0,
  maxSites: 1,
  maxStorageGB: 1,
  maxBandwidthGB: 10,
  maxEnvironments: 1,
  allowStaging: false,
  allowProduction: true,
  isActive: true,
  // Resource limits
  wpCpuLimit: 0.5,
  wpMemoryLimitMb: 256,
  wpDbCpuLimit: 0.25,
  wpDbMemoryLimitMb: 256,
  appCpuLimit: 0.25,
  appMemoryLimitMb: 128,
  maxApps: 1,
  maxServicesPerApp: 2,
  maxVolumesPerSvc: 2,
  maxBackupRetention: 3,
  maxSftpUsersPerSite: 1,
  maxSshKeys: 2,
  maxApiTokens: 2,
};

const AdminPlans = () => {
  const dispatch = useDispatch();
  const { plans, error, successMessage } = useSelector((state) => state.admin);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState(emptyPlan);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchAdminPlans());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearAdminSuccess()), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearAdminError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setFormData(emptyPlan);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || "",
      priceMonthly: plan.priceMonthly || 0,
      priceYearly: plan.priceYearly || 0,
      trialDays: plan.trialDays || 0,
      maxSites: plan.maxSites || 1,
      maxStorageGB: plan.maxStorageGB || 1,
      maxBandwidthGB: plan.maxBandwidthGB || 10,
      maxEnvironments: plan.maxEnvironments || 1,
      allowStaging: plan.allowStaging || false,
      allowProduction: plan.allowProduction !== false,
      isActive: plan.isActive !== false,
      // Resource limits
      wpCpuLimit: plan.wpCpuLimit ?? 0.5,
      wpMemoryLimitMb: plan.wpMemoryLimitMb ?? 256,
      wpDbCpuLimit: plan.wpDbCpuLimit ?? 0.25,
      wpDbMemoryLimitMb: plan.wpDbMemoryLimitMb ?? 256,
      appCpuLimit: plan.appCpuLimit ?? 0.25,
      appMemoryLimitMb: plan.appMemoryLimitMb ?? 128,
      maxApps: plan.maxApps ?? 1,
      maxServicesPerApp: plan.maxServicesPerApp ?? 2,
      maxVolumesPerSvc: plan.maxVolumesPerSvc ?? 2,
      maxBackupRetention: plan.maxBackupRetention ?? 3,
      maxSftpUsersPerSite: plan.maxSftpUsersPerSite ?? 1,
      maxSshKeys: plan.maxSshKeys ?? 2,
      maxApiTokens: plan.maxApiTokens ?? 2,
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (editingPlan) {
      dispatch(updateAdminPlan({ planId: editingPlan.id, planData: formData }));
    } else {
      dispatch(createAdminPlan(formData));
    }
    setDialogOpen(false);
  };

  const handleDelete = (planId) => {
    setDeleteTarget(planId);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            Plans Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage subscription plans and pricing
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
        >
          Add Plan
        </Button>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminSuccess())}
        >
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminError())}
        >
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Max Sites</TableCell>
                  <TableCell>Storage</TableCell>
                  <TableCell>Bandwidth</TableCell>
                  <TableCell>Environments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subscribers</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!plans || plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No plans found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {plan.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${Number(plan.priceMonthly || 0).toFixed(2)}/mo
                        </Typography>
                        {Number(plan.priceYearly) > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            ${Number(plan.priceYearly).toFixed(2)}/yr
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{plan.maxSites}</TableCell>
                      <TableCell>{plan.maxStorageGB} GB</TableCell>
                      <TableCell>{plan.maxBandwidthGB} GB</TableCell>
                      <TableCell>{plan.maxEnvironments}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={plan.isActive ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.subscriberCount || 0}
                          size="small"
                          color={
                            plan.subscriberCount > 0 ? "primary" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(plan)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(plan.id)}
                          disabled={plan.subscriberCount > 0}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {editingPlan ? "Edit Plan" : "Create Plan"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Basic Info */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Basic Info
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Max Sites"
                  type="number"
                  value={formData.maxSites}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxSites: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Storage (GB)"
                  type="number"
                  value={formData.maxStorageGB}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxStorageGB: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Bandwidth (GB)"
                  type="number"
                  value={formData.maxBandwidthGB}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxBandwidthGB: parseInt(e.target.value) || 10,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Environments"
                  type="number"
                  value={formData.maxEnvironments}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxEnvironments: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowStaging}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowStaging: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Allow Staging"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowProduction}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowProduction: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Allow Production"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            {/* Pricing & Billing */}
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Pricing & Billing
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Price ($/mo)"
                  type="number"
                  value={formData.priceMonthly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priceMonthly: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Price ($/yr)"
                  type="number"
                  value={formData.priceYearly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priceYearly: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Trial Days"
                  type="number"
                  value={formData.trialDays}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trialDays: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* WordPress Resource Limits */}
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              WordPress Resource Limits (per site)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="WP CPU Limit"
                  type="number"
                  inputProps={{ step: 0.25 }}
                  value={formData.wpCpuLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wpCpuLimit: parseFloat(e.target.value) || 0.5,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="WP Memory (MB)"
                  type="number"
                  value={formData.wpMemoryLimitMb}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wpMemoryLimitMb: parseInt(e.target.value) || 256,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="DB CPU Limit"
                  type="number"
                  inputProps={{ step: 0.25 }}
                  value={formData.wpDbCpuLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wpDbCpuLimit: parseFloat(e.target.value) || 0.25,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="DB Memory (MB)"
                  type="number"
                  value={formData.wpDbMemoryLimitMb}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wpDbMemoryLimitMb: parseInt(e.target.value) || 256,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* App Resource Limits */}
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              App Resource Limits (per service)
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="App CPU Limit"
                  type="number"
                  inputProps={{ step: 0.25 }}
                  value={formData.appCpuLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      appCpuLimit: parseFloat(e.target.value) || 0.25,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="App Memory (MB)"
                  type="number"
                  value={formData.appMemoryLimitMb}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      appMemoryLimitMb: parseInt(e.target.value) || 128,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Apps"
                  type="number"
                  value={formData.maxApps}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxApps: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Services/App"
                  type="number"
                  value={formData.maxServicesPerApp}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxServicesPerApp: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Max Volumes/Service"
                  type="number"
                  value={formData.maxVolumesPerSvc}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxVolumesPerSvc: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* Quantity Limits */}
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Quantity Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Backup Retention"
                  type="number"
                  value={formData.maxBackupRetention}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxBackupRetention: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="SFTP Users/Site"
                  type="number"
                  value={formData.maxSftpUsersPerSite}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxSftpUsersPerSite: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Max SSH Keys"
                  type="number"
                  value={formData.maxSshKeys}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxSshKeys: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Max API Tokens"
                  type="number"
                  value={formData.maxApiTokens}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxApiTokens: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name}
          >
            {editingPlan ? "Update Plan" : "Create Plan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this plan?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            onClick={() => {
              dispatch(deleteAdminPlan(deleteTarget));
              setDeleteTarget(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPlans;
